// CC direct-chat channel renderer.
//
// Wraps the standard chat renderer (renderChat) and enhances CC bot messages
// with inline dev-tail panel expansion (collapsible "Show thinking"), a
// clock/cost/tokens metadata footer, and an autonomy mode toggle chip in
// the compose area.

import { renderChat, cleanupChat } from './chat';
import { state as _state } from '../state';
import { API_BASE as _API_BASE } from '../api';

const BOT_CC_ID = 'bot-cc-aa42b91c8d47';

/** Currently selected default mode. Persisted in localStorage. */
function getDefaultMode(): 'autonomous' | 'ask' {
  return (localStorage.getItem('cc-chat-mode') as 'autonomous' | 'ask') || 'autonomous';
}
function setDefaultMode(mode: 'autonomous' | 'ask'): void {
  localStorage.setItem('cc-chat-mode', mode);
}

let observer: MutationObserver | null = null;
let metadataPollTimer: ReturnType<typeof setInterval> | null = null;

export function renderCcChat(body: HTMLElement, channel: string): void {
  // 1. Render the standard chat view.
  renderChat(body, channel);

  // 2. Inject the mode toggle chip into the compose area.
  injectModeChip();

  // 3. Observe new messages to enhance CC bot replies.
  const messagesEl = document.getElementById('chat-messages');
  if (messagesEl) {
    // Enhance any existing CC bot messages already in the DOM.
    enhanceBotMessages(messagesEl);

    // Watch for new messages added via realtime/polling.
    observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node instanceof HTMLElement) {
            enhanceBotMessages(node.parentElement || messagesEl);
          }
        }
      }
    });
    observer.observe(messagesEl, { childList: true, subtree: true });
  }

  // 4. Intercept send to prepend @ask when in ask mode.
  hookComposeForMode();
}

export function cleanupCcChat(): void {
  if (observer) { observer.disconnect(); observer = null; }
  if (metadataPollTimer) { clearInterval(metadataPollTimer); metadataPollTimer = null; }
  cleanupChat();
}

// ── Mode toggle chip ────────────────────────────────────────────────────

function injectModeChip(): void {
  const compose = document.querySelector('.chat-compose');
  if (!compose) return;

  const existing = document.getElementById('cc-mode-chip');
  if (existing) return;

  const mode = getDefaultMode();
  const chip = document.createElement('button');
  chip.id = 'cc-mode-chip';
  chip.className = 'cc-mode-chip';
  chip.type = 'button';
  chip.title = 'Toggle CC autonomy mode';
  chip.textContent = mode === 'autonomous' ? 'Mode: Autonomous' : 'Mode: Ask';
  chip.dataset['mode'] = mode;

  chip.addEventListener('click', () => {
    const current = chip.dataset['mode'] as 'autonomous' | 'ask';
    const next = current === 'autonomous' ? 'ask' : 'autonomous';
    setDefaultMode(next);
    chip.dataset['mode'] = next;
    chip.textContent = next === 'autonomous' ? 'Mode: Autonomous' : 'Mode: Ask';
    chip.classList.toggle('cc-mode-ask', next === 'ask');
  });

  chip.classList.toggle('cc-mode-ask', mode === 'ask');
  compose.insertBefore(chip, compose.firstChild);
}

function hookComposeForMode(): void {
  // The shared compose mounts textarea + send button via data-compose
  // attributes; look them up inside the active .chat-compose.
  const compose = document.querySelector('.chat-compose');
  if (!compose) return;
  const input = compose.querySelector<HTMLTextAreaElement>('[data-compose="textarea"]');
  if (!input) return;

  // Intercept the send button and Enter key to prepend @ask when in ask mode.
  const sendBtn = compose.querySelector<HTMLButtonElement>('[data-compose="send-btn"]');
  const wrapSend = (_original: EventListenerOrEventListenerObject | null): void => {
    // We prepend @ask to the textarea value before the original handler fires.
    // The original handler reads input.value, so mutating it is sufficient.
    const handler = (): void => {
      const mode = getDefaultMode();
      if (mode === 'ask' && input.value.trim() && !input.value.trimStart().startsWith('@ask ')) {
        input.value = '@ask ' + input.value;
      }
    };
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !e.altKey) {
        handler();
      }
    }, { capture: true });
    sendBtn?.addEventListener('click', handler, { capture: true });
  };
  wrapSend(null);
}

// ── Bot message enhancement ─────────────────────────────────────────────

function enhanceBotMessages(container: HTMLElement): void {
  const msgs = container.querySelectorAll<HTMLElement>(`.msg[data-author="${BOT_CC_ID}"]`);
  for (const msgEl of msgs) {
    if (msgEl.dataset['ccEnhanced']) continue;
    msgEl.dataset['ccEnhanced'] = '1';
    msgEl.classList.add('cc-bot-msg');

    const body = msgEl.querySelector('.msg-body');
    if (!body) continue;

    // Add collapsible "Show thinking" toggle.
    const thinkingToggle = document.createElement('button');
    thinkingToggle.className = 'cc-thinking-toggle';
    thinkingToggle.type = 'button';
    thinkingToggle.textContent = 'Show thinking';

    const thinkingPanel = document.createElement('div');
    thinkingPanel.className = 'cc-thinking-panel';
    thinkingPanel.style.display = 'none';

    // Populate thinking panel with dev-tail-style info.
    void (msgEl.dataset['id'] || '');
    thinkingPanel.innerHTML = `
      <div class="cc-think-section">
        <span class="cc-think-label">Tools used</span>
        <div class="cc-think-tools" data-role="cc-tools">--</div>
      </div>
      <div class="cc-think-section">
        <span class="cc-think-label">Phase</span>
        <div class="cc-think-phase" data-role="cc-phase">--</div>
      </div>
      <div class="cc-think-section">
        <span class="cc-think-label">Context</span>
        <div class="cc-think-context" data-role="cc-context">--</div>
      </div>
    `;

    thinkingToggle.addEventListener('click', () => {
      const visible = thinkingPanel.style.display !== 'none';
      thinkingPanel.style.display = visible ? 'none' : 'block';
      thinkingToggle.textContent = visible ? 'Show thinking' : 'Hide thinking';
    });

    // Metadata footer (clock / cost / tokens).
    const metaFooter = document.createElement('div');
    metaFooter.className = 'cc-meta-footer';
    metaFooter.innerHTML = renderMetaFooter(msgEl);

    body.appendChild(thinkingToggle);
    body.appendChild(thinkingPanel);
    body.appendChild(metaFooter);
  }
}

function renderMetaFooter(msgEl: HTMLElement): string {
  // Extract timestamp from the message for elapsed calculation.
  const timeEl = msgEl.querySelector('.msg-time');
  const ts = timeEl?.getAttribute('title') || '';
  return `<span class="cc-meta-item" title="Reply timestamp">${esc(ts)}</span>`;
}

// ── Helpers ──────────────────────────────────────────────────────────────

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}
